import React from "react";
import SeoLanding from "./SeoLanding";

export default function ApprendreLeJaponais() {
  return (
    <SeoLanding
      testid="seo-apprendre"
      title="Apprendre le japonais en ligne : Méthode 15 min/jour | Japonais Pas à Pas"
      description="Apprendre le japonais facilement avec une méthode 100% francophone. 200 leçons interactives N5 → N3, tuteur IA, audio natif. Essai gratuit 3 jours, sans carte bancaire."
      keywords="apprendre le japonais, apprendre japonais, apprendre le japonais facilement, apprendre le japonais gratuit, apprendre le japonais seul, apprendre le japonais débutant, méthode pour apprendre le japonais"
      canonical="https://japonais-pas-a-pas.com/apprendre-le-japonais"
      h1="Apprendre le japonais en 15 min par jour, sans douleur"
      subtitle="La méthode joyeuse et structurée pour francophones. Du JLPT N5 au N3, sans manuel rébarbatif."
      lead="Tu veux apprendre le japonais mais tu ne sais pas par où commencer ? Tu as déjà essayé une app de langue qui t'a laissé tomber au bout de deux semaines ? Japonais Pas à Pas est conçue pour celles et ceux qui veulent apprendre le japonais sérieusement, à leur rythme, avec une méthode claire et un vrai accompagnement IA. Sans s'épuiser, sans culpabiliser."
      sections={[
        {
          h2: "Apprendre le japonais quand on est francophone : nos 3 piliers",
          paragraphs: [
            "<strong>Apprendre le japonais</strong> nécessite trois choses : une bonne méthode, de l'audio natif, et de la régularité. La plupart des plateformes proposent l'une ou l'autre. Nous combinons les trois.",
          ],
          bullets: [
            "<strong>Une méthode francophone</strong> : tout est expliqué en français, sans détour par l'anglais. Les nuances culturelles franco-japonaises sont expliquées par Atsuko, japonaise native vivant en France",
            "<strong>Un audio 100% natif</strong> : Atsuko (féminine) et Alexis (masculin) prononcent chaque kana, chaque mot, chaque dialogue. Plus jamais de prononciation chinoise ou anglo sur tes kanji",
            "<strong>Un système de régularité</strong> : 15 minutes par jour, des rappels emails doux, un système de révision intelligente. Le but n'est pas de tout retenir d'un coup, c'est de ne rien oublier",
          ],
        },
        {
          h2: "Apprendre le japonais gratuitement : est-ce possible ?",
          paragraphs: [
            "Oui, jusqu'à un certain point. Tu peux <strong>apprendre les hiragana et katakana gratuitement</strong> sur notre plateforme (page Kana publique, page Vocabulaire publique avec 636 mots, blog avec articles pédagogiques). Tu peux aussi télécharger gratuitement notre PDF de 50 phrases japonaises pour voyager.",
            "Pour le reste, les 200 leçons structurées, le tuteur IA, le système de révision SRS, les examens blancs JLPT : l'essai gratuit 3 jours te donne accès à tout, sans carte bancaire. Si après quelques jours la méthode te convient, l'abonnement reste à un prix très accessible (12,90 €/mois).",
          ],
        },
        {
          h2: "Apprendre le japonais seul : nos conseils",
          paragraphs: [
            "<strong>Apprendre le japonais seul</strong>, c'est faisable mais traître. Sans cadre, on s'éparpille, on saute des étapes, et au final on stagne. Voici ce qui a marché pour nos premiers utilisateurs :",
          ],
          bullets: [
            "<strong>Démarrer par les hiragana, pas le romaji</strong> : le romaji est une béquille qui t'empêche de progresser. Une semaine sur les hiragana et tu y vois clair",
            "<strong>Limiter le vocabulaire à apprendre par jour</strong> : 5 à 10 mots nouveaux suffisent. Mieux vaut bien ancrer 10 mots que survoler 50",
            "<strong>Écouter avant de parler</strong> : écoute les dialogues en boucle, même sans tout comprendre. Ton oreille s'habitue aux sons (intonation, longueur des voyelles, sokuon)",
            "<strong>Pratiquer tous les jours, même 5 minutes</strong> : la régularité bat l'intensité. Un mois à 15 min/jour > 4 sessions de 2h par mois",
          ],
        },
        {
          h2: "Combien de temps pour apprendre le japonais ?",
          paragraphs: [
            "À raison de 15 minutes par jour, voici les jalons réalistes pour <strong>apprendre le japonais</strong> avec notre méthode :",
          ],
          bullets: [
            "<strong>1 semaine</strong> : hiragana maîtrisés (46 caractères + sons composés)",
            "<strong>2 semaines</strong> : katakana maîtrisés (les 46 caractères pour les mots étrangers)",
            "<strong>3 mois</strong> : niveau JLPT N5 (te présenter, conversations simples, ~100 kanji)",
            "<strong>9-12 mois</strong> : niveau JLPT N4 (lire des manga shōnen, comprendre des dialogues quotidiens)",
            "<strong>2 ans</strong> : niveau JLPT N3 (lire des articles, comprendre la majorité des films sous-titrés)",
          ],
        },
      ]}
      faq={[
        {
          q: "Faut-il être doué en langues pour apprendre le japonais ?",
          a: "Non. Le japonais a la réputation d'être difficile à cause des kanji, mais la grammaire est en fait plus simple que celle du français. Notre méthode te guide pas à pas, sans présupposer de talent particulier.",
        },
        {
          q: "Combien de temps faut-il par jour pour vraiment progresser ?",
          a: "15 minutes par jour suffisent si tu es régulier(ère). C'est mieux que 2 heures d'un coup le week-end. Notre format de leçons courtes est calibré pour ce rythme.",
        },
        {
          q: "Puis-je apprendre le japonais sans connaître les kanji ?",
          a: "Au début oui. Tu commences par les hiragana et katakana (1-2 semaines). Les kanji arrivent ensuite, par petits paquets de 10-15 par semaine. Au bout de 3 mois, tu connais 100 kanji.",
        },
        {
          q: "Y a-t-il des outils pour pratiquer la conversation ?",
          a: "Oui. Notre tuteur IA en japonais et français te permet d'avoir des conversations écrites, de poser des questions de grammaire, de demander des exemples. Pour la conversation orale, l'audio natif Atsuko/Alexis te sert de modèle de prononciation.",
        },
        {
          q: "Et après le N3, que se passe-t-il ?",
          a: "Le N3 te donne déjà un niveau intermédiaire confortable. Nous travaillons sur l'extension N2 et N1, mais en attendant, tu peux pratiquer via du vrai contenu japonais (mangas, animés, livres) que notre tuteur IA peut t'expliquer.",
        },
      ]}
    />
  );
}
